import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';

const RealtimeTerminal = () => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [currentRoom, setCurrentRoom] = useState('kuber_atv_room');
  const [roomIdInput, setRoomIdInput] = useState('kuber_atv_room');
  const [events, setEvents] = useState([]);
  const [ackFilter, setAckFilter] = useState('all');
  const dataLogRef = useRef(null);

  // Initialize socket connection
  const initializeSocket = () => {
    if (socket && socket.connected) {
      socket.disconnect();
    }

    const newSocket = io('https://realtime-data.gotrasoft.com', {
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
      timeout: 10000,
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      console.log('[REALTIME_TERMINAL] Connected to server:', newSocket.id);
      if (currentRoom) {
        newSocket.emit('join_room', { room_id: currentRoom });
      }
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
      console.log('[REALTIME_TERMINAL] Disconnected:', reason);
    });

    newSocket.on('connect_error', (error) => {
      setIsConnected(false);
      console.error('[REALTIME_TERMINAL] Connection error:', error);
    });

    newSocket.on('new_data', (data) => {
      console.log('[REALTIME_TERMINAL] New data received:', data);
      if (data.room_id === currentRoom) {
        setEvents(prev => [data, ...prev]);
      }
    });

    newSocket.on('data_history', (response) => {
      console.log('[REALTIME_TERMINAL] Data history received:', response);
      if (response.room_id === currentRoom && Array.isArray(response.history)) {
        setEvents(response.history);
      }
    });

    newSocket.on('event_acknowledged', (data) => {
      console.log('[REALTIME_TERMINAL] Event acknowledged:', data);
      setEvents(prev => prev.map(event =>
        event.id === data.event_id
          ? { ...event, ack: 1 }
          : event
      ));
    });

    newSocket.on('room_joined', (response) => {
      if (response.success) {
        console.log('[REALTIME_TERMINAL] Successfully joined room:', response.room_id);
        // Request history after joining
        newSocket.emit('request_history', {
          room_id: currentRoom,
          limit: 100,
          ack_status: ackFilter
        });
      } else {
        console.error('[REALTIME_TERMINAL] Failed to join room:', response.message);
      }
    });

    setSocket(newSocket);
  };

  // Join room
  const joinRoom = () => {
    const roomId = roomIdInput.trim();
    if (!roomId) {
      alert('Room ID tidak boleh kosong!');
      return;
    }

    setCurrentRoom(roomId);
    localStorage.setItem('realtime_last_room_id', roomId);

    if (!socket || !socket.connected) {
      initializeSocket();
    } else {
      socket.emit('join_room', { room_id: roomId });
    }
  };

  // Leave room
  const leaveRoom = () => {
    if (socket && currentRoom) {
      socket.emit('leave_room', { room_id: currentRoom });
    }
    setCurrentRoom(null);
    setEvents([]);
  };

  // Request history
  const requestHistory = () => {
    if (socket && socket.connected && currentRoom) {
      socket.emit('request_history', {
        room_id: currentRoom,
        limit: 100,
        ack_status: ackFilter
      });
    }
  };

  // Acknowledge event
  const acknowledgeEvent = async (eventId) => {
    try {
      const response = await fetch('https://realtime-data.gotrasoft.com/api/acknowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ event_id: eventId }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to acknowledge event');
      }

      console.log('[REALTIME_TERMINAL] Event acknowledged successfully:', eventId);
    } catch (error) {
      console.error('[REALTIME_TERMINAL] Failed to acknowledge event:', error);
      alert(`Gagal acknowledge event: ${error.message}`);
    }
  };

  // Clear log
  const clearLog = () => {
    setEvents([]);
  };

  // Filter events based on ack status
  const filteredEvents = events.filter(event => {
    if (ackFilter === 'all') return true;
    if (ackFilter === '0') return event.ack == 0; // Unread
    if (ackFilter === '1') return event.ack == 1; // Read
    return true;
  });

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (dataLogRef.current) {
      dataLogRef.current.scrollTop = dataLogRef.current.scrollHeight;
    }
  }, [filteredEvents]);

  // Load last room ID from localStorage
  useEffect(() => {
    const lastRoomId = localStorage.getItem('realtime_last_room_id');
    if (lastRoomId) {
      setRoomIdInput(lastRoomId);
    }
  }, []);

  // Initialize socket on mount
  useEffect(() => {
    initializeSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  // Request history when filter changes
  useEffect(() => {
    if (currentRoom) {
      requestHistory();
    }
  }, [ackFilter, currentRoom]);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp || Date.now()).toLocaleString('id-ID', {
      dateStyle: 'short',
      timeStyle: 'medium'
    });
  };

  return (
    <div className="realtime-terminal bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Realtime Data Terminal</h2>

      {/* Connection Setup */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Masukkan Room ID:
        </label>
        <div className="flex gap-3">
          <input
            type="text"
            value={roomIdInput}
            onChange={(e) => setRoomIdInput(e.target.value)}
            placeholder="Contoh: kuber_atv_room"
            className="flex-grow p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
          />
          <button
            onClick={joinRoom}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-5 rounded-lg transition duration-150 text-sm"
          >
            Gabung Room
          </button>
        </div>
        {currentRoom && (
          <button
            onClick={leaveRoom}
            className="mt-3 w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 px-4 rounded-lg transition duration-150 text-sm"
          >
            Keluar Room & Ganti
          </button>
        )}
      </div>

      {/* Status Messages */}
      <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
        <p>Status Koneksi: <span className={`font-semibold ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
          {isConnected ? 'Terhubung' : 'Belum Terhubung'}
        </span></p>
        <p>Room Aktif: <span className="font-semibold text-blue-600">{currentRoom || '-'}</span></p>
      </div>

      {/* Data Log */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-700">Log Data Diterima:</h3>
          <div className="flex items-center gap-4">
            <fieldset className="flex items-center gap-3 border border-gray-200 p-1.5 rounded-lg">
              <legend className="text-xs font-medium text-gray-500 px-1">Filter Status:</legend>
              <div>
                <input
                  type="radio"
                  id="filter-all"
                  name="ack_filter"
                  value="all"
                  checked={ackFilter === 'all'}
                  onChange={(e) => setAckFilter(e.target.value)}
                  className="mr-1"
                />
                <label htmlFor="filter-all" className="text-sm">All</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="filter-unread"
                  name="ack_filter"
                  value="0"
                  checked={ackFilter === '0'}
                  onChange={(e) => setAckFilter(e.target.value)}
                  className="mr-1"
                />
                <label htmlFor="filter-unread" className="text-sm">Unread</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="filter-read"
                  name="ack_filter"
                  value="1"
                  checked={ackFilter === '1'}
                  onChange={(e) => setAckFilter(e.target.value)}
                  className="mr-1"
                />
                <label htmlFor="filter-read" className="text-sm">Read</label>
              </div>
            </fieldset>
            <button
              onClick={clearLog}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 text-xs"
            >
              Bersihkan Log
            </button>
          </div>
        </div>

        <div
          ref={dataLogRef}
          className="h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50"
        >
          {filteredEvents.length === 0 ? (
            <p className="text-gray-500 italic text-center py-8">
              {currentRoom ? 'Menunggu data...' : 'Bergabung ke room terlebih dahulu'}
            </p>
          ) : (
            filteredEvents.map((event) => (
              <div
                key={event.id}
                className={`log-entry border-b border-gray-200 pb-3 mb-3 ${
                  event.ack == 1 ? 'is-read opacity-75' : ''
                }`}
              >
                <div className="flex justify-between items-center mb-1 text-xs">
                  <div>
                    <span className="text-gray-500">{formatTimestamp(event.timestamp)}</span> -
                    ID: <span className="font-mono text-purple-500">{event.id || 'N/A'}</span> -
                    Event: <span className="text-blue-500 font-medium">{event.type}</span> -
                    Room: <span className="text-teal-500 font-medium">{event.room_id}</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full font-semibold ${
                      event.ack == 1 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {event.ack == 1 ? 'READ' : 'UNREAD'}
                    </span>
                  </div>
                  {event.ack == 0 && (
                    <button
                      onClick={() => acknowledgeEvent(event.id)}
                      className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold py-1 px-3 rounded-full ml-4"
                    >
                      Acknowledge
                    </button>
                  )}
                </div>
                <pre className="bg-gray-800 text-gray-200 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(event.payload, null, 2)}
                </pre>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RealtimeTerminal;